import { useCallback, useEffect, useState } from 'react';
import { services as servicesApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can, money } from '../components/Common';

/**
 * Icons are stored on the category as a plain emoji string, so the mobile app
 * renders whatever is chosen here with no asset hosting and no app release.
 */
const ICON_CHOICES = [
  '👔', '🧺', '🧻', '👕', '♨️', '👟', '🟫', '🪟', '🛏️', '✨',
  '🧥', '👗', '🥼', '🧣', '🧦', '👖', '🩳', '🧢', '👜', '🧸',
  '🚿', '🫧', '🧼', '🧽', '🪣', '🧴', '⚡', '🕐', '💎', '🏠',
];

const TABS = [
  { key: 'categories', label: 'Categories' },
  { key: 'items', label: 'Service Items' },
];

/**
 * Turnaround presets. Dry cleaning realistically needs 72 hours; only wash &
 * fold and wash & iron turn around in 48. A custom value is still allowed.
 */
const TURNAROUND_CHOICES = [
  { hours: 12, label: '12 hours (same day)' },
  { hours: 24, label: '24 hours (1 day)' },
  { hours: 48, label: '48 hours (2 days)' },
  { hours: 72, label: '72 hours (3 days)' },
  { hours: 96, label: '96 hours (4 days)' },
  { hours: 120, label: '120 hours (5 days)' },
];

const turnaroundLabel = (hours) => {
  if (!hours) return '—';
  const days = hours / 24;
  return Number.isInteger(days) ? `${days} day${days === 1 ? '' : 's'}` : `${hours} hours`;
};

const FALLBACK_ICON = '🧼';

/**
 * An emoji is short and contains no ASCII letters or digits.
 *
 * Older records stored icon *names* ("hanger", "washer"), which rendered as
 * overflowing text in the tile. Anything that is not emoji-shaped falls back to
 * a default rather than breaking the layout.
 */
function iconOf(category) {
  const value = category?.icon;
  const isEmoji = Boolean(value) && value.length <= 8 && !/[A-Za-z0-9]/.test(value);
  return isEmoji ? value : FALLBACK_ICON;
}

export default function Services() {
  const toast = useToast();

  const [tab, setTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // `isActive` is left unset so the admin sees hidden records too.
      const [cats, its] = await Promise.all([servicesApi.categories(), servicesApi.items()]);
      setCategories(cats.data.categories);
      setItems(its.data.items);
    } catch (err) {
      toast.error('Could not load the catalogue', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Services · UrbanClean'; }, []);

  if (loading) return <Loading label="Loading catalogue" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Services</h1>
          <div className="sub">{categories.length} categories · {items.length} items</div>
        </div>
      </div>

      <div className="toolbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'categories'
        ? <CategoriesTab categories={categories} items={items} reload={load} />
        : <ItemsTab categories={categories} items={items} reload={load} />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Categories                                                                  */
/* -------------------------------------------------------------------------- */

function CategoriesTab({ categories, items, reload }) {
  const toast = useToast();
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  /**
   * Show / hide in the app. The switch writes straight through to the server and
   * only then re-reads, so what is on screen is always what is stored.
   */
  const toggleVisible = async (category) => {
    setBusyId(category._id);
    try {
      const next = !(category.isActive !== false);
      await servicesApi.updateCategory(category._id, { isActive: next });
      toast.success(next ? `${category.name} is visible in the app` : `${category.name} is hidden`);
      await reload();
    } catch (err) {
      toast.error('Could not change visibility', err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (category) => {
    if (!window.confirm(`Delete the category "${category.name}"?`)) return;
    try {
      await servicesApi.removeCategory(category._id);
      toast.success('Category deleted');
      reload();
    } catch (err) {
      toast.error('Could not delete', err.message);
    }
  };

  return (
    <>
      <div className="toolbar">
        <div className="spacer" />
        <Can permission="service.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New category</button>
        </Can>
      </div>

      {categories.length === 0 ? (
        <div className="card">
          <Empty title="No categories yet" body="Create one, pick its icon, then add the items inside it." />
        </div>
      ) : (
        <div className="grid grid-3">
          {categories.map((category) => {
            const count = items.filter((i) => (i.category?._id || i.category) === category._id).length;
            const visible = category.isActive !== false;
            const prices = items
              .filter((i) => (i.category?._id || i.category) === category._id)
              .map((i) => i.price);

            return (
              <div className="card" key={category._id} style={{ opacity: visible ? 1 : 0.72 }}>
                <div className="card-body">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div
                      style={{
                        width: 54, height: 54, borderRadius: 13, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 27, lineHeight: 1, overflow: 'hidden',
                        background: `${category.color || '#3182C8'}1A`,
                        border: `1px solid ${category.color || '#3182C8'}33`,
                      }}
                    >
                      {iconOf(category)}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="strong" style={{ fontSize: 15, color: 'var(--navy)' }}>
                        {category.name}
                      </div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                        {category.description || 'No description'}
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <span className="badge badge-gray">{count} item{count === 1 ? '' : 's'}</span>
                        {prices.length ? (
                          <span className="badge badge-blue">from {money(Math.min(...prices))}</span>
                        ) : null}
                        <span className={`badge badge-${visible ? 'green' : 'gray'}`}>
                          {visible ? 'Visible' : 'Hidden'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 10, marginTop: 15, paddingTop: 13, borderTop: '1px solid var(--line)',
                    }}
                  >
                    <Can
                      permission="service.update"
                      fallback={<span className="muted" style={{ fontSize: 12 }}>View only</span>}
                    >
                      <label className="switch" style={{ opacity: busyId === category._id ? 0.5 : 1 }}>
                        <input
                          type="checkbox"
                          checked={visible}
                          disabled={busyId === category._id}
                          onChange={() => toggleVisible(category)}
                        />
                        <span className="track" />
                        <span style={{ fontSize: 12.5, fontWeight: 600 }}>Show in app</span>
                      </label>
                    </Can>

                    <div style={{ display: 'flex', gap: 6 }}>
                      <Can permission="service.update">
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(category)}>Edit</button>
                      </Can>
                      <Can permission="service.delete">
                        <button
                          className="btn btn-sm btn-ghost"
                          style={{ color: 'var(--red)' }}
                          onClick={() => remove(category)}
                        >
                          Delete
                        </button>
                      </Can>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <CategoryEditor
        category={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </>
  );
}

/** A category, blank or existing. Defaults live here so the form is never uncontrolled. */
const BLANK_CATEGORY = {
  name: '', description: '', icon: '🧼', color: '#3182C8', sortOrder: 0, isActive: true,
};

function CategoryEditor({ category, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(BLANK_CATEGORY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!category) return;
    setForm({
      name: category.name || '',
      description: category.description || '',
      // A legacy text name is replaced by its emoji rather than shown as text.
      icon: iconOf(category),
      color: category.color || '#3182C8',
      sortOrder: category.sortOrder ?? 0,
      // `!== false` so a brand-new category defaults to visible.
      isActive: category.isActive !== false,
    });
  }, [category]);

  if (!category) return null;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give the category a name');
    setBusy(true);
    try {
      const body = { ...form, sortOrder: Number(form.sortOrder) || 0 };
      if (category._id) await servicesApi.updateCategory(category._id, body);
      else await servicesApi.createCategory(body);
      toast.success(category._id ? 'Category updated' : 'Category created');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title={category._id ? `Edit — ${category.name}` : 'New category'}
      onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save category'}
        </button>
      </>}
    >
      <div className="row">
        <div className="field">
          <label>Name *</label>
          <input className="input" value={form.name} onChange={set('name')} placeholder="Dry Cleaning" />
        </div>
        <div className="field">
          <label>Sort order</label>
          <input className="input" type="number" value={form.sortOrder} onChange={set('sortOrder')} />
        </div>
      </div>

      <div className="field">
        <label>Description</label>
        <input className="input" value={form.description} onChange={set('description')} />
      </div>

      <div className="field">
        <label>Icon</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div
            style={{
              width: 56, height: 56, borderRadius: 13, display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 28,
              background: `${form.color}1A`, border: `1px solid ${form.color}33`,
            }}
          >
            {iconOf(form)}
          </div>
          <div style={{ flex: 1 }}>
            <input
              className="input"
              value={form.icon}
              onChange={set('icon')}
              placeholder="Pick one below, or paste any emoji"
              style={{ fontSize: 17 }}
            />
            <div className="hint">This is exactly what the app shows on the service tile.</div>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ICON_CHOICES.map((icon) => (
            <button
              key={icon}
              type="button"
              onClick={() => setForm((f) => ({ ...f, icon }))}
              title={`Use ${icon}`}
              style={{
                width: 38, height: 38, fontSize: 19, cursor: 'pointer',
                borderRadius: 8,
                background: form.icon === icon ? 'var(--blue-100)' : 'var(--surface)',
                border: `1.5px solid ${form.icon === icon ? 'var(--blue)' : 'var(--line)'}`,
              }}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label>Accent colour</label>
        <input className="input" type="color" value={form.color} onChange={set('color')} style={{ height: 42, padding: 4, maxWidth: 120 }} />
        <div className="hint">Tints the icon tile in the app.</div>
      </div>

      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
          <span className="track" /><span>Visible in the app</span>
        </label>
        <div className="hint">Hidden categories keep their items but disappear from the customer app.</div>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Items                                                                       */
/* -------------------------------------------------------------------------- */

function ItemsTab({ categories, items, reload }) {
  const toast = useToast();
  const [activeCategory, setActiveCategory] = useState('');
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const visible = activeCategory
    ? items.filter((i) => (i.category?._id || i.category) === activeCategory)
    : items;

  const toggleVisible = async (item) => {
    setBusyId(item._id);
    try {
      const next = !(item.isActive !== false);
      await servicesApi.updateItem(item._id, { isActive: next });
      toast.success(next ? `${item.name} is bookable` : `${item.name} is hidden`);
      await reload();
    } catch (err) {
      toast.error('Could not change visibility', err.message);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await servicesApi.removeItem(item._id);
      toast.success('Item deleted');
      reload();
    } catch (err) {
      toast.error('Could not delete', err.message);
    }
  };

  return (
    <>
      <div className="toolbar">
        <button
          className={`btn btn-sm ${!activeCategory ? 'btn-primary' : 'btn-ghost'}`}
          onClick={() => setActiveCategory('')}
        >
          All ({items.length})
        </button>
        {categories.map((c) => {
          const count = items.filter((i) => (i.category?._id || i.category) === c._id).length;
          return (
            <button
              key={c._id}
              className={`btn btn-sm ${activeCategory === c._id ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveCategory(c._id)}
            >
              <span style={{ marginRight: 5 }}>{iconOf(c)}</span>
              {c.name} ({count})
            </button>
          );
        })}
        <div className="spacer" />
        <Can permission="service.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ New item</button>
        </Can>
      </div>

      <div className="card">
        {visible.length === 0 ? (
          <Empty title="No service items here" body="Add one to make it bookable in the app." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Item</th><th>Category</th><th>Unit</th>
                  <th className="right">Price</th><th className="right">Express</th>
                  <th className="center">Ready in</th><th>Show in app</th><th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => {
                  const shown = item.isActive !== false;
                  return (
                    <tr key={item._id}>
                      <td>
                        <div className="strong">{item.name}</div>
                        {item.description ? <div className="muted" style={{ fontSize: 12 }}>{item.description}</div> : null}
                      </td>
                      <td className="muted">
                        <span style={{ marginRight: 5 }}>{iconOf(item.category)}</span>
                        {item.category?.name}
                      </td>
                      <td className="muted">per {item.unit}</td>
                      <td className="right strong">{money(item.price)}</td>
                      <td className="right muted">{item.expressPrice ? money(item.expressPrice) : '—'}</td>
                      <td className="center muted">{turnaroundLabel(item.turnaroundHours)}</td>
                      <td>
                        <Can
                          permission="service.update"
                          fallback={<span className={`badge badge-${shown ? 'green' : 'gray'}`}>{shown ? 'Visible' : 'Hidden'}</span>}
                        >
                          <label className="switch" style={{ opacity: busyId === item._id ? 0.5 : 1 }}>
                            <input
                              type="checkbox"
                              checked={shown}
                              disabled={busyId === item._id}
                              onChange={() => toggleVisible(item)}
                            />
                            <span className="track" />
                          </label>
                        </Can>
                      </td>
                      <td className="right nowrap">
                        <Can permission="service.update">
                          <button className="btn btn-sm btn-ghost" onClick={() => setEditing(item)}>Edit</button>
                        </Can>
                        <Can permission="service.delete">
                          <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(item)}>
                            Delete
                          </button>
                        </Can>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ItemEditor
        item={editing}
        categories={categories}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </>
  );
}

const BLANK_ITEM = {
  name: '', description: '', category: '', unit: 'piece',
  price: '', expressPrice: '', turnaroundHours: 48, isActive: true,
};

function ItemEditor({ item, categories, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(BLANK_ITEM);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item) return;
    setForm({
      name: item.name || '',
      description: item.description || '',
      category: item.category?._id || item.category || (categories[0]?._id ?? ''),
      unit: item.unit || 'piece',
      price: item.price ?? '',
      expressPrice: item.expressPrice ?? '',
      turnaroundHours: item.turnaroundHours ?? 48,
      isActive: item.isActive !== false,
    });
  }, [item, categories]);

  if (!item) return null;

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.name || form.price === '') return toast.error('Name and price are required');
    setBusy(true);
    try {
      const body = {
        ...form,
        price: Number(form.price),
        expressPrice: form.expressPrice === '' ? null : Number(form.expressPrice),
        turnaroundHours: Number(form.turnaroundHours) || 48,
      };
      if (item._id) await servicesApi.updateItem(item._id, body);
      else await servicesApi.createItem(body);
      toast.success('Service item saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={item._id ? `Edit — ${item.name}` : 'New service item'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </>}>
      <div className="row">
        <div className="field"><label>Name *</label><input className="input" value={form.name} onChange={set('name')} /></div>
        <div className="field">
          <label>Category *</label>
          <select className="select" value={form.category} onChange={set('category')}>
            {categories.map((c) => <option key={c._id} value={c._id}>{iconOf(c)} {c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="field"><label>Description</label><input className="input" value={form.description} onChange={set('description')} /></div>

      <div className="row">
        <div className="field">
          <label>Unit</label>
          <select className="select" value={form.unit} onChange={set('unit')}>
            {['piece', 'kg', 'sqft', 'pair', 'set'].map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div className="field"><label>Price *</label><input className="input" type="number" value={form.price} onChange={set('price')} /></div>
        <div className="field"><label>Express price</label><input className="input" type="number" value={form.expressPrice} onChange={set('expressPrice')} /></div>
      </div>

      <div className="row">
        <div className="field">
          <label>Ready in *</label>
          <select
            className="select"
            value={TURNAROUND_CHOICES.some((t) => t.hours === Number(form.turnaroundHours)) ? form.turnaroundHours : 'custom'}
            onChange={(e) => {
              if (e.target.value === 'custom') return;
              setForm((f) => ({ ...f, turnaroundHours: Number(e.target.value) }));
            }}
          >
            {TURNAROUND_CHOICES.map((t) => <option key={t.hours} value={t.hours}>{t.label}</option>)}
            <option value="custom">Custom…</option>
          </select>
          <div className="hint">Shown to the customer on this service in the app.</div>
        </div>
        <div className="field">
          <label>Or exact hours</label>
          <input className="input" type="number" min={1} value={form.turnaroundHours} onChange={set('turnaroundHours')} />
        </div>
      </div>

      <div className="hint" style={{ marginTop: -8, marginBottom: 16 }}>
        Taxes are set once for all items under Settings → Taxes &amp; Charges.
      </div>

      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
          <span className="track" /><span>Bookable in the app</span>
        </label>
      </div>
    </Modal>
  );
}
